/**
 * usePreviewIo — owns the side-channel fetches the preview modal needs
 * but that don't drive any UI directly:
 *
 *   - the page-transitions catalogue (one fetch per `open=true`)
 *
 * Centralised here so the modal's main effect tree stays focused on
 * playback + composition.
 *
 * Also exposes a thin helper `resolveActiveTransitionId` that the
 * `transitions/resolveActiveTransition` module reuses (kept in the IO
 * layer because the resolution requires the catalogue result).
 */
import { useEffect, useState } from 'react';
import { adminApi } from '@/lib/adminApi';
import type { ProposalTransition } from '@/lib/transitions/catalogue';

export interface PreviewIoState {
  transitions: ProposalTransition[];
  transitionsLoading: boolean;
  transitionsError: string | null;
}

export function usePreviewIo(open: boolean): PreviewIoState {
  const [transitions, setTransitions] = useState<ProposalTransition[]>([]);
  const [transitionsLoading, setTransitionsLoading] = useState(false);
  const [transitionsError, setTransitionsError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setTransitionsLoading(true);
    adminApi
      .get<{ success: boolean; transitions: ProposalTransition[] }>(
        '/admin/transitions',
      )
      .then((res) => {
        if (cancelled) return;
        setTransitionsLoading(false);
        if (res.success) {
          setTransitions(res.transitions);
          setTransitionsError(null);
        }
      })
      .catch((e: { message?: string; status?: number }) => {
        if (cancelled) return;
        setTransitionsLoading(false);
        setTransitionsError(e?.message ?? 'Failed to load transitions');
        // eslint-disable-next-line no-console
        console.warn(
          '[proposal-preview] transitions fetch failed',
          e?.message,
          e?.status,
        );
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  return { transitions, transitionsLoading, transitionsError };
}
